import { useRef, useState, useEffect, useCallback } from 'react';
import { Article } from '../hooks/useArticles';
import { AudioPlayer } from './AudioPlayer';
import { useSwipeGesture } from '../hooks/useSwipeGesture';

interface ArticleReaderProps {
  article: Article;
  onBack: () => void;
  onProgressUpdate?: (progress: number) => void;
}

export function ArticleReader({ article, onBack, onProgressUpdate }: ArticleReaderProps) {
  const readerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [readingProgress, setReadingProgress] = useState(article.readingProgress || 0);
  const [, setHighlightedSentence] = useState<number | null>(null);
  const lastSavedProgress = useRef(article.readingProgress || 0);

  // Swipe right to go back
  useSwipeGesture(readerRef, {
    threshold: 100,
    onSwipeRight: onBack
  });

  // Debounced progress save
  const saveProgress = useCallback((progress: number) => {
    if (onProgressUpdate && Math.abs(progress - lastSavedProgress.current) >= 5) {
      lastSavedProgress.current = progress;
      onProgressUpdate(progress);
    }
  }, [onProgressUpdate]);

  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const element = contentRef.current;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      const clampedProgress = Math.min(100, Math.max(0, progress));

      setReadingProgress(clampedProgress);
      saveProgress(clampedProgress);
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll, { passive: true });
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, [saveProgress]);

  // Restore scroll position based on saved progress
  useEffect(() => {
    if (contentRef.current && article.readingProgress > 0) {
      const element = contentRef.current;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const scrollTop = (article.readingProgress / 100) * scrollHeight;
      element.scrollTop = scrollTop;
    }
  }, [article.readingProgress]);

  // Parse markdown-like content into HTML
  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let currentParagraph: string[] = [];
    let key = 0;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const text = currentParagraph.join(' ');
        elements.push(<p key={key++}>{formatInlineText(text)}</p>);
        currentParagraph = [];
      }
    };

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Headings
      if (trimmedLine.startsWith('## ')) {
        flushParagraph();
        elements.push(<h2 key={key++}>{trimmedLine.slice(3)}</h2>);
      } else if (trimmedLine.startsWith('# ')) {
        flushParagraph();
        elements.push(<h1 key={key++}>{trimmedLine.slice(2)}</h1>);
      }
      // Empty line = paragraph break
      else if (trimmedLine === '') {
        flushParagraph();
      }
      // Regular text
      else {
        currentParagraph.push(trimmedLine);
      }
    }

    flushParagraph();
    return elements;
  };

  // Format inline markdown (bold, italic, code)
  const formatInlineText = (text: string): React.ReactNode => {
    // Simple regex-based formatting
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Bold
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
      // Inline code
      const codeMatch = remaining.match(/`([^`]+)`/);

      if (boldMatch && (!codeMatch || boldMatch.index! < codeMatch.index!)) {
        const before = remaining.slice(0, boldMatch.index);
        if (before) parts.push(before);
        parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch.index! + boldMatch[0].length);
      } else if (codeMatch) {
        const before = remaining.slice(0, codeMatch.index);
        if (before) parts.push(before);
        parts.push(<code key={key++}>{codeMatch[1]}</code>);
        remaining = remaining.slice(codeMatch.index! + codeMatch[0].length);
      } else {
        parts.push(remaining);
        break;
      }
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  const handleSentenceChange = (index: number) => {
    setHighlightedSentence(index);
    // Could scroll to highlighted sentence here if needed
  };

  return (
    <div className="article-reader" ref={readerRef}>
      {/* Reading progress bar */}
      <div className="reading-progress-bar">
        <div
          className="reading-progress-fill"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <header className="reader-header">
        <button className="back-btn" onClick={onBack} aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="reader-actions">
          <button
            className={`action-btn ${showAudioPlayer ? 'active' : ''}`}
            onClick={() => setShowAudioPlayer(!showAudioPlayer)}
            aria-label="Toggle audio player"
            title="Listen to article"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="reader-content" ref={contentRef}>
        <article className="article-content">
          <h1>{article.title}</h1>
          <div className="article-meta">
            <span>{article.readingTime} min read</span>
            <span>-</span>
            <span>{new Date(article.savedAt).toLocaleDateString()}</span>
          </div>
          {article.tags.length > 0 && (
            <div className="article-tags">
              {article.tags.map((tag) => (
                <span key={tag} className="tag">{tag}</span>
              ))}
            </div>
          )}
          <div className="article-body">
            {renderContent(article.content)}
          </div>
        </article>
      </div>

      {/* Audio Player */}
      {showAudioPlayer && (
        <AudioPlayer
          content={article.content}
          title={article.title}
          onSentenceChange={handleSentenceChange}
        />
      )}
    </div>
  );
}
